import type { ReactNode } from 'react';
import type { JSONContent } from '@tiptap/react';
import {
  Lock,
  Menu,
  RotateCw,
  ShoppingBag,
  ShoppingCart,
  Store,
} from 'lucide-react';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { IosLockScreenNotification } from '@/components/campaign-composer/ios-lock-screen-notification';
import {
  formatLockScreenClock,
  formatLockScreenDate,
} from '@/components/campaign-composer/korza-studio';
import { CouponBannerPreview } from '@/components/coupon-banner/coupon-banner-preview';
import { StoreAppScene } from '@/components/coupon-banner/store-app-scene';
import { welcomeArrival } from '@/components/welcome-notification/welcome-studio';
import type { CreateWebsiteBannerDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import type { CouponPosition } from '@/lib/app-editor/coupon-banner-form';
import type { WelcomeDelay } from '@/lib/app-editor/welcome-notification-form';
import { cn } from '@/lib/utils';
import { bannerPlainText } from '@/lib/website-banner-constants';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import { LockScreenScene } from '@/pages/onboarding/mockup/scenes/lock-screen-scene';

// Thumbnails of the hub cards: where each feature shows up, cropped from the
// onboarding's real iPhone (the whole device scaled, never a look-alike).
// `compact` is the mobile row's narrow thumbnail: the device bigger and
// cropped at the sides, zoomed in on the feature.

const SCREEN_WIDTH = 300;
const BACKGROUND =
  'radial-gradient(80% 90% at 50% 0%, #FFFFFF 0%, #E9ECF1 100%)';

function PhoneCrop({
  edge,
  compact,
  sceneKey,
  chromeBaked = false,
  children,
}: {
  /** The end of the device shown: its top (status bar) or its bottom. */
  edge: 'top' | 'bottom';
  compact: boolean;
  sceneKey: string;
  chromeBaked?: boolean;
  children: ReactNode;
}) {
  // Compact: from the device's left edge (bezel and corner in view), cropped
  // at the right, so the thumbnail reads as a phone zoomed in.
  const scale = compact ? 0.55 : 0.8;
  const originX = compact ? '0' : '50%';
  return (
    <div className="absolute inset-0" style={{ background: BACKGROUND }}>
      <div
        className={cn(
          'absolute drop-shadow-[0_20px_24px_rgba(11,52,140,0.22)]',
          compact ? 'left-1.5' : 'left-1/2',
          edge === 'top' ? (compact ? 'top-3.5' : 'top-[22px]') : 'bottom-3.5'
        )}
        style={{
          transform: `${compact ? '' : 'translateX(-50%) '}scale(${scale})`,
          transformOrigin: `${originX} ${edge === 'top' ? '0' : '100%'}`,
        }}
      >
        <IPhoneMockup
          sceneKey={sceneKey}
          screenWidth={SCREEN_WIDTH}
          chromeBaked={chromeBaked}
        >
          {children}
        </IPhoneMockup>
      </div>
    </div>
  );
}

function plainDoc(text: string): JSONContent {
  return { type: 'doc', content: [{ type: 'text', text }] };
}

export interface CouponGlimpseProps {
  storeName: string;
  iconUrl: string | null | undefined;
  /** The saved banner, or `null` before it is configured (a sample shows). */
  banner: {
    bannerText: JSONContent;
    backgroundColorHex: string;
    foregroundColorHex: string;
    position: CouponPosition;
  } | null;
  compact: boolean;
}

/** 01: the store's app with the coupon banner, at its saved position. */
export function CouponGlimpse({
  storeName,
  iconUrl,
  banner,
  compact,
}: CouponGlimpseProps) {
  const { LL } = useI18nContext();
  const shown = banner ?? {
    bannerText: plainDoc(LL.appHub.glimpse.couponSample()),
    backgroundColorHex: '#19304D',
    foregroundColorHex: '#FFFFFF',
    position: 'top' as const,
  };
  // Still in a thumbnail: four scrolling strips would pull the eye.
  const strip = (
    <CouponBannerPreview
      bannerText={shown.bannerText}
      backgroundColorHex={shown.backgroundColorHex}
      foregroundColorHex={shown.foregroundColorHex}
      animated={false}
      position={shown.position}
    />
  );
  const top = shown.position === 'top';

  return (
    <PhoneCrop edge={shown.position} compact={compact} sceneKey="hub-coupon">
      <StoreAppScene
        storeName={storeName}
        iconUrl={iconUrl}
        top={top ? strip : undefined}
        bottom={top ? undefined : strip}
      />
    </PhoneCrop>
  );
}

export interface WelcomeGlimpseProps {
  title: string;
  body: string;
  iconUrl: string | null | undefined;
  delayMinutes: WelcomeDelay;
  compact: boolean;
}

/** 02: the lock screen with the welcome push, at the time it arrives. */
export function WelcomeGlimpse({
  title,
  body,
  iconUrl,
  delayMinutes,
  compact,
}: WelcomeGlimpseProps) {
  const { LL, locale } = useI18nContext();
  const arrival = welcomeArrival(delayMinutes);

  return (
    <PhoneCrop
      edge="bottom"
      compact={compact}
      sceneKey="hub-welcome"
      chromeBaked
    >
      <div className="relative h-full w-full">
        <LockScreenScene
          dateLabel={formatLockScreenDate(arrival, locale)}
          clock={formatLockScreenClock(arrival, locale)}
        />
        <IosLockScreenNotification
          title={title}
          body={body}
          titlePlaceholder={LL.notificationPreview.titlePlaceholder()}
          bodyPlaceholder={LL.notificationPreview.bodyPlaceholder()}
          timeLabel={LL.notificationPreview.now()}
          iconUrl={iconUrl}
          screenWidth={SCREEN_WIDTH}
          visible
          reduceMotion
        />
      </div>
    </PhoneCrop>
  );
}

export interface SiteGlimpseProps {
  iconUrl: string | null | undefined;
  domain: string;
  content: CreateWebsiteBannerDto['content'];
  theme: CreateWebsiteBannerDto['theme'];
  compact: boolean;
}

/** 03: the store's website in Safari, with the app banner on top. */
export function SiteGlimpse({
  iconUrl,
  domain,
  content,
  theme,
  compact,
}: SiteGlimpseProps) {
  return (
    <PhoneCrop edge="top" compact={compact} sceneKey="hub-site">
      <div className="flex h-full w-full flex-col bg-white text-[#111111]">
        <div className="h-[46px] shrink-0" />
        <div className="mx-2.5 mb-2 flex h-[34px] shrink-0 items-center gap-2 rounded-xl bg-[#EFEFF2] px-3 text-[13px]">
          <span className="text-xs font-semibold">aA</span>
          <span className="flex min-w-0 flex-1 items-center justify-center gap-[5px]">
            <Lock aria-hidden strokeWidth={2.6} className="size-2.5 shrink-0" />
            <span className="truncate">{domain}</span>
          </span>
          <RotateCw aria-hidden strokeWidth={2} className="size-3.5 shrink-0" />
        </div>
        <div
          className="flex shrink-0 items-center gap-2 px-2.5 py-2 text-[10.5px] leading-snug"
          style={{ backgroundColor: theme.bgColor, color: theme.fgColor }}
        >
          <span className="min-w-0 flex-1">
            {bannerPlainText(content.textHtml)}
          </span>
          <span
            className="shrink-0 rounded px-2 py-1 text-[9px] font-bold"
            style={{ backgroundColor: theme.fgColor, color: theme.bgColor }}
          >
            {content.buttonText}
          </span>
        </div>
        <div className="flex h-11 shrink-0 items-center justify-between border-b border-[#EEEEEE] px-3.5">
          <Menu aria-hidden strokeWidth={1.6} className="size-5" />
          {/* The store's mark only: a long name would not fit uncut. */}
          {iconUrl ? (
            <img
              src={iconUrl}
              alt=""
              className="size-[26px] shrink-0 rounded-md object-cover"
            />
          ) : (
            <Store aria-hidden className="size-5 shrink-0" />
          )}
          <ShoppingBag aria-hidden strokeWidth={1.6} className="size-5" />
        </div>
        <div
          className="h-32 shrink-0"
          style={{
            background: 'linear-gradient(135deg, #DDE0E5 0%, #C6CBD2 100%)',
          }}
        />
      </div>
    </PhoneCrop>
  );
}

/** 04: coming soon, on the brand book's grainy blue. */
export function CartGlimpse({ compact }: { compact: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#15191E]">
      <div
        className="absolute -inset-[10%]"
        style={{
          background:
            'radial-gradient(55% 50% at 80% 15%, rgba(21,93,252,0.9), transparent 72%), radial-gradient(55% 45% at 10% 95%, rgba(150,185,255,0.5), transparent 70%), linear-gradient(165deg, #0B348C 0%, #15191E 78%)',
        }}
      />
      <GrainOverlay />
      {compact ? (
        <span className="absolute left-1/2 top-1/2 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl bg-white/[.14] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]">
          <ShoppingCart
            aria-hidden
            strokeWidth={1.5}
            className="size-[22px] text-white"
          />
        </span>
      ) : (
        <div className="absolute inset-x-3.5 top-1/2 flex -translate-y-1/2 items-center gap-2.5 rounded-[18px] bg-white/[.12] px-3 py-[11px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]">
          <span className="flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-white/[.16]">
            <ShoppingCart
              aria-hidden
              strokeWidth={1.5}
              className="size-4 text-white"
            />
          </span>
          <span className="flex flex-1 flex-col gap-1.5">
            <span className="h-[7px] w-[70%] rounded bg-white/50" />
            <span className="h-[7px] w-1/2 rounded bg-white/[.28]" />
          </span>
        </div>
      )}
    </div>
  );
}
